<template>
  <div class="min-h-screen bg-gray-950 p-6 bg-grid-pattern">
    <!-- Background effects -->
    <div class="fixed inset-0 overflow-hidden pointer-events-none">
      <div
        class="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute -bottom-40 -left-40 w-80 h-80 bg-accent-red/10 rounded-full blur-3xl"
      ></div>
    </div>

    <div class="relative max-w-7xl mx-auto space-y-6">
      <!-- Header -->
      <div class="card">
        <div class="px-6 py-4">
          <div class="flex items-center justify-between">
            <div>
              <h1 class="text-2xl font-bold text-white font-display">Быстрый старт</h1>
              <p class="text-gray-400 mt-1">
                Интегрируйте pushx.pro в ваше приложение за 5 минут
              </p>
            </div>
            <a
              href="https://pushx.pro/docs"
              target="_blank"
              class="btn-secondary inline-flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
              </svg>
              Полная документация
            </a>
          </div>
        </div>
      </div>

      <!-- Application Selector + Keys -->
      <div class="card">
        <div class="px-6 py-4 border-b border-gray-800">
          <h2 class="text-lg font-semibold text-white font-display">Ваши ключи API</h2>
        </div>
        <div class="px-6 py-5">
          <!-- Loading state -->
          <div v-if="loading" class="flex items-center justify-center py-8">
            <svg class="animate-spin h-8 w-8 text-primary-500" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>

          <!-- No apps state -->
          <div v-else-if="applications.length === 0" class="text-center py-8">
            <svg class="w-12 h-12 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z"></path>
            </svg>
            <p class="text-gray-400 mb-4">У вас пока нет приложений</p>
            <router-link to="/applications" class="btn-primary">
              Создать приложение
            </router-link>
          </div>

          <!-- App selector and keys -->
          <div v-else class="space-y-5">
            <!-- App selector -->
            <div>
              <label class="form-label">Выберите приложение</label>
              <select
                v-model="selectedAppId"
                class="input-field"
              >
                <option
                  v-for="app in applications"
                  :key="app.id"
                  :value="app.id"
                >
                  {{ app.name }}
                </option>
              </select>
            </div>

            <!-- Keys display -->
            <div v-if="selectedApp" class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- App Key -->
              <div class="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-sm text-gray-400">App Key</span>
                  <button
                    @click="copyToClipboard(selectedApp.key, 'App Key')"
                    class="text-gray-400 hover:text-primary-400 transition-colors"
                    title="Копировать"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <code class="text-primary-400 font-mono text-sm break-all">{{ selectedApp.key }}</code>
              </div>

              <!-- App Secret -->
              <div class="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-sm text-gray-400">App Secret</span>
                  <div class="flex items-center gap-2">
                    <button
                      @click="toggleSecret"
                      :disabled="secretLoading"
                      class="text-gray-400 hover:text-primary-400 transition-colors disabled:opacity-50"
                      :title="showSecret ? 'Скрыть' : 'Показать'"
                    >
                      <svg v-if="!showSecret" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                      </svg>
                      <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path>
                      </svg>
                    </button>
                    <button
                      @click="copySecret"
                      class="text-gray-400 hover:text-primary-400 transition-colors"
                      title="Копировать"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                      </svg>
                    </button>
                  </div>
                </div>
                <code class="text-accent-red font-mono text-sm break-all">
                  {{ showSecret && revealedSecret ? revealedSecret : '••••••••••••••••••••••••' }}
                </code>
                <p v-if="!showSecret" class="mt-2 text-xs text-gray-500">
                  Нажмите «Показать», чтобы подставить secret в примеры кода ниже
                </p>
              </div>
            </div>

            <!-- Warning -->
            <div class="bg-accent-yellow/10 border border-accent-yellow/30 rounded-xl p-4">
              <div class="flex items-start gap-3">
                <svg class="w-5 h-5 text-accent-yellow flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
                <p class="text-sm text-accent-yellow">
                  <strong>Важно:</strong> App Secret используется только на сервере. Никогда не передавайте его в клиентский код!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SDK Tabs -->
      <div v-if="selectedApp" class="card overflow-hidden">
        <div class="border-b border-gray-800">
          <nav class="flex -mb-px overflow-x-auto scrollbar-hide">
            <button
              @click="activeTab = 'js'"
              :class="[
                'px-5 py-4 text-sm font-medium border-b-2 transition-all duration-200 whitespace-nowrap flex items-center gap-2',
                activeTab === 'js'
                  ? 'border-primary-500 text-primary-500 bg-primary-500/5'
                  : 'border-transparent text-gray-400 hover:text-gray-200 hover:bg-gray-800/50',
              ]"
            >
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M0 0h24v24H0V0zm22.034 18.276c-.175-1.095-.888-2.015-3.003-2.873-.736-.345-1.554-.585-1.797-1.14-.091-.33-.105-.51-.046-.705.15-.646.915-.84 1.515-.66.39.12.75.42.976.9 1.034-.676 1.034-.676 1.755-1.125-.27-.42-.404-.601-.586-.78-.63-.705-1.469-1.065-2.834-1.034l-.705.089c-.676.165-1.32.525-1.71 1.005-1.14 1.291-.811 3.541.569 4.471 1.365 1.02 3.361 1.244 3.616 2.205.24 1.17-.87 1.545-1.966 1.41-.811-.18-1.26-.586-1.755-1.336l-1.83 1.051c.21.48.45.689.81 1.109 1.74 1.756 6.09 1.666 6.871-1.004.029-.09.24-.705.074-1.65l.046.067zm-8.983-7.245h-2.248c0 1.938-.009 3.864-.009 5.805 0 1.232.063 2.363-.138 2.711-.33.689-1.18.601-1.566.48-.396-.196-.597-.466-.83-.855-.063-.105-.11-.196-.127-.196l-1.825 1.125c.305.63.75 1.172 1.324 1.517.855.51 2.004.675 3.207.405.783-.226 1.458-.691 1.811-1.411.51-.93.402-2.07.397-3.346.012-2.054 0-4.109 0-6.179l.004-.056z"/>
              </svg>
              JavaScript
            </button>
            <button
              @click="activeTab = 'php'"
              :class="[
                'px-5 py-4 text-sm font-medium border-b-2 transition-all duration-200 whitespace-nowrap flex items-center gap-2',
                activeTab === 'php'
                  ? 'border-primary-500 text-primary-500 bg-primary-500/5'
                  : 'border-transparent text-gray-400 hover:text-gray-200 hover:bg-gray-800/50',
              ]"
            >
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.01 10.207h-.944l-.515 2.648h.838c.556 0 .97-.105 1.242-.314.272-.21.455-.559.55-1.049.092-.47.05-.802-.124-.995-.175-.193-.523-.29-1.047-.29zM12 5.688C5.373 5.688 0 8.514 0 12s5.373 6.313 12 6.313S24 15.486 24 12c0-3.486-5.373-6.312-12-6.312zm-3.26 7.451c-.261.25-.575.438-.917.551-.336.108-.765.164-1.285.164H5.357l-.327 1.681H3.652l1.23-6.326h2.65c.797 0 1.378.209 1.744.628.366.418.476 1.002.33 1.752a2.836 2.836 0 0 1-.866 1.55zm5.14-.366c-.111.569-.31 1.08-.596 1.533a2.63 2.63 0 0 1-1.006.96c-.386.217-.842.326-1.37.326h-1.329l-.327 1.681h-1.378l1.23-6.326h2.649c.797 0 1.378.209 1.744.628.366.418.477 1.002.33 1.752-.034.173-.078.344-.132.505a3.04 3.04 0 0 1-.815-.059zm4.83-1.085h-.944l-.515 2.648h.838c.557 0 .971-.105 1.242-.314.272-.21.455-.559.55-1.049.092-.47.05-.802-.124-.995-.175-.193-.523-.29-1.047-.29z"/>
              </svg>
              PHP
            </button>
            <button
              @click="activeTab = 'python'"
              :class="[
                'px-5 py-4 text-sm font-medium border-b-2 transition-all duration-200 whitespace-nowrap flex items-center gap-2',
                activeTab === 'python'
                  ? 'border-primary-500 text-primary-500 bg-primary-500/5'
                  : 'border-transparent text-gray-400 hover:text-gray-200 hover:bg-gray-800/50',
              ]"
            >
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14.25.18l.9.2.73.26.59.3.45.32.34.34.25.34.16.33.1.3.04.26.02.2-.01.13V8.5l-.05.63-.13.55-.21.46-.26.38-.3.31-.33.25-.35.19-.35.14-.33.1-.3.07-.26.04-.21.02H8.77l-.69.05-.59.14-.5.22-.41.27-.33.32-.27.35-.2.36-.15.37-.1.35-.07.32-.04.27-.02.21v3.06H3.17l-.21-.03-.28-.07-.32-.12-.35-.18-.36-.26-.36-.36-.35-.46-.32-.59-.28-.73-.21-.88-.14-1.05-.05-1.23.06-1.22.16-1.04.24-.87.32-.71.36-.57.4-.44.42-.33.42-.24.4-.16.36-.1.32-.05.24-.01h.16l.06.01h8.16v-.83H6.18l-.01-2.75-.02-.37.05-.34.11-.31.17-.28.25-.26.31-.23.38-.2.44-.18.51-.15.58-.12.64-.1.71-.06.77-.04.84-.02 1.27.05zm-6.3 1.98l-.23.33-.08.41.08.41.23.34.33.22.41.09.41-.09.33-.22.23-.34.08-.41-.08-.41-.23-.33-.33-.22-.41-.09-.41.09zm13.09 3.95l.28.06.32.12.35.18.36.27.36.35.35.47.32.59.28.73.21.88.14 1.04.05 1.23-.06 1.23-.16 1.04-.24.86-.32.71-.36.57-.4.45-.42.33-.42.24-.4.16-.36.09-.32.05-.24.02-.16-.01h-8.22v.82h5.84l.01 2.76.02.36-.05.34-.11.31-.17.29-.25.25-.31.24-.38.2-.44.17-.51.15-.58.13-.64.09-.71.07-.77.04-.84.01-1.27-.04-1.07-.14-.9-.2-.73-.25-.59-.3-.45-.33-.34-.34-.25-.34-.16-.33-.1-.3-.04-.25-.02-.2.01-.13v-5.34l.05-.64.13-.54.21-.46.26-.38.3-.32.33-.24.35-.2.35-.14.33-.1.3-.06.26-.04.21-.02.13-.01h5.84l.69-.05.59-.14.5-.21.41-.28.33-.32.27-.35.2-.36.15-.36.1-.35.07-.32.04-.28.02-.21V6.07h2.09l.14.01zm-6.47 14.25l-.23.33-.08.41.08.41.23.33.33.23.41.08.41-.08.33-.23.23-.33.08-.41-.08-.41-.23-.33-.33-.23-.41-.08-.41.08z"/>
              </svg>
              Python
            </button>
            <button
              @click="activeTab = 'csharp'"
              :class="[
                'px-5 py-4 text-sm font-medium border-b-2 transition-all duration-200 whitespace-nowrap flex items-center gap-2',
                activeTab === 'csharp'
                  ? 'border-primary-500 text-primary-500 bg-primary-500/5'
                  : 'border-transparent text-gray-400 hover:text-gray-200 hover:bg-gray-800/50',
              ]"
            >
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0zM9.426 7.12a5.55 5.55 0 011.985.38v1.181a4.5 4.5 0 00-2.25-.566 3.439 3.439 0 00-2.625 1.087 4.099 4.099 0 00-1.012 2.906 3.9 3.9 0 00.945 2.754 3.217 3.217 0 002.482 1.023 4.657 4.657 0 002.464-.634l-.004 1.08a5.543 5.543 0 01-2.625.555 4.211 4.211 0 01-3.228-1.297 4.793 4.793 0 01-1.212-3.409 5.021 5.021 0 011.365-3.663 4.631 4.631 0 013.473-1.392c.074-.003.165-.004.242-.004zm5.158 2.069l.273 1.381h1.5v.721h-1.352l.327 1.758h1.5v.721h-1.352l.36 1.917h-.96l-.36-1.917h-1.623l.36 1.917h-.96l-.361-1.917H10.49v-.721h1.59l-.34-1.758h-1.5v-.721h1.352l-.3-1.381h.96l.3 1.381h1.573l-.3-1.381zm.156 2.102h-1.622l.34 1.758h1.608z"/>
              </svg>
              C#
            </button>
            <button
              @click="activeTab = 'java'"
              :class="[
                'px-5 py-4 text-sm font-medium border-b-2 transition-all duration-200 whitespace-nowrap flex items-center gap-2',
                activeTab === 'java'
                  ? 'border-primary-500 text-primary-500 bg-primary-500/5'
                  : 'border-transparent text-gray-400 hover:text-gray-200 hover:bg-gray-800/50',
              ]"
            >
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8.851 18.56s-.917.534.653.714c1.902.218 2.874.187 4.969-.211 0 0 .552.346 1.321.646-4.699 2.013-10.633-.118-6.943-1.149M8.276 15.933s-1.028.761.542.924c2.032.209 3.636.227 6.413-.308 0 0 .384.389.987.602-5.679 1.661-12.007.13-7.942-1.218M13.116 11.475c1.158 1.333-.304 2.533-.304 2.533s2.939-1.518 1.589-3.418c-1.261-1.772-2.228-2.652 3.007-5.688 0-.001-8.216 2.051-4.292 6.573M19.33 20.504s.679.559-.747.991c-2.712.822-11.288 1.069-13.669.033-.856-.373.75-.89 1.254-.998.527-.114.828-.93.828-.093-953.692-1.509-4.334 2.173-.035 3.123 11.024 2.436 20.094-1.099 12.369-3.056M9.292 13.21s-3.073.731-1.088 996c.833.111 2.493.086 4.042-.044 1.266-.106 2.536-.332 2.536-.332s-.447.191-.77.411c-3.111.818-9.123.437-7.394-.399 1.461-.707 2.674-.632 2.674-.632M17.127 17.774c3.164-1.644 1.701-3.225.68-3.011-.25.053-.361.098-.361.098s.093-.145.27-.208c2.015-.708 3.564 2.089-.652 3.198 0 0 .049-.044.063-.077M14.401 0s1.752 1.752-1.661 4.448c-2.735 2.162-.623 3.393 0 4.8-1.595-1.441-2.769-2.708-1.983-3.887 1.155-1.734 4.355-2.573 3.644-5.361M9.734 23.924c3.038.195 7.705-.108 7.814-1.548 0 0-.212.545-2.515.977-2.594.487-5.793.429-7.688.118 0-.001.39.323 2.389.453"/>
              </svg>
              Java
            </button>
            <button
              @click="activeTab = 'swift'"
              :class="[
                'px-5 py-4 text-sm font-medium border-b-2 transition-all duration-200 whitespace-nowrap flex items-center gap-2',
                activeTab === 'swift'
                  ? 'border-primary-500 text-primary-500 bg-primary-500/5'
                  : 'border-transparent text-gray-400 hover:text-gray-200 hover:bg-gray-800/50',
              ]"
            >
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.508 0c-.287 0-.573 0-.86.002-.241.002-.483.003-.724.01-.132.003-.263.009-.395.015A9.154 9.154 0 004.045.2a4.917 4.917 0 00-.723.2A4.623 4.623 0 002.061 1.02a4.632 4.632 0 00-.879.63 4.58 4.58 0 00-.62.878 4.624 4.624 0 00-.62 1.261A9.087 9.087 0 00.199 5.528c-.006.133-.012.264-.015.397-.007.24-.008.482-.01.724C.002 6.935.002 7.221.002 7.508v8.984c0 .287 0 .575.002.862.002.24.003.482.01.722.003.133.009.265.015.398a9.087 9.087 0 00.172 1.484 4.64 4.64 0 00.619 1.261c.177.271.389.531.62.879.25.254.521.488.879.62a4.622 4.622 0 001.26.619c.239.062.483.118.728.171a9.21 9.21 0 001.484.173c.132.006.263.012.395.015.241.007.483.008.724.01.287.002.573.002.86.002h8.984c.287 0 .573 0 .86-.002.241-.002.483-.003.724-.01.132-.003.263-.009.395-.015a9.21 9.21 0 001.484-.172 4.897 4.897 0 00.729-.172c.434-.143.846-.357 1.26-.619.271-.177.531-.389.879-.62.254-.25.488-.521.62-.879.26-.412.474-.824.619-1.26.063-.24.118-.484.172-.729a9.089 9.089 0 00.173-1.484c.006-.132.012-.264.015-.397.007-.24.008-.482.01-.723.002-.287.002-.575.002-.861V7.508c0-.287 0-.573-.002-.86-.002-.241-.003-.483-.01-.724a19.92 19.92 0 00-.015-.395 9.089 9.089 0 00-.173-1.484 4.897 4.897 0 00-.172-.729 4.623 4.623 0 00-.619-1.26 4.58 4.58 0 00-.62-.879 4.623 4.623 0 00-.879-.62 4.917 4.917 0 00-1.26-.618A9.154 9.154 0 0017.753.2a19.91 19.91 0 00-.396-.015c-.24-.007-.482-.008-.723-.01C16.347.002 16.061.002 15.774 0zm6.035 3.606c2.48 1.527 4.127 4.168 4.327 7.118.047.703.018 1.396-.076 2.074-.127.916-.393 1.79-.772 2.606-.209.451-.447.885-.716 1.299-.129.199-.266.393-.412.58a10.166 10.166 0 01-.121.15c-.053.065-.11.128-.165.19a13.94 13.94 0 01-.985 1.017 13.015 13.015 0 01-4.765 2.925 12.96 12.96 0 01-2.992.671c-.14.014-.281.026-.422.035a10.39 10.39 0 01-.306.018c-.053.002-.106.003-.16.004a10.162 10.162 0 01-.16.002h-.16a10.29 10.29 0 01-.16-.002c-.054-.001-.107-.002-.16-.004a10.39 10.39 0 01-.319-.018 12.962 12.962 0 01-2.893-.587c.391.016.782.002 1.172-.041a8.598 8.598 0 002.209-.509 8.676 8.676 0 001.923-.96 11.285 11.285 0 01-2.418-1.446c-.9-.67-1.72-1.455-2.428-2.337-.5-.624-.942-1.295-1.332-2.002-.39-.707-.725-1.45-1.002-2.215a11.857 11.857 0 01-.59-2.312c.303.298.628.57.973.814a11.294 11.294 0 002.256 1.269 12.42 12.42 0 002.468.843c.398.096.8.175 1.206.234l.035.005a8.047 8.047 0 01-.135-.367c-.115-.344-.217-.694-.287-1.051a5.727 5.727 0 01-.058-.33 6.116 6.116 0 01-.058-.664c-.015-.482.033-.947.138-1.388.089-.378.218-.74.38-1.084.086-.182.18-.36.282-.531.227-.38.502-.73.822-1.039.122-.119.249-.232.381-.34a6.178 6.178 0 01.424-.316 6.94 6.94 0 01.553-.344c.202-.114.41-.218.624-.312A7.015 7.015 0 019.93 5.05c.254-.055.511-.098.77-.128.26-.03.521-.046.783-.049.255-.003.511.009.765.035.254.026.507.067.757.121a6.94 6.94 0 011.443.453c.137.057.27.12.402.185.131.066.26.136.387.21l.042.025.039.025c.013.008.026.017.038.026l.078.054.077.056.073.054a8.5 8.5 0 01.211.166l.002.002.006.005.013.01.025.021.05.043.048.043.023.02.011.01.006.006.003.002v.001l.001.001.048.044.048.044.012.012.006.005.003.003"/>
              </svg>
              Swift
            </button>
            <button
              @click="activeTab = 'go'"
              :class="[
                'px-5 py-4 text-sm font-medium border-b-2 transition-all duration-200 whitespace-nowrap flex items-center gap-2',
                activeTab === 'go'
                  ? 'border-primary-500 text-primary-500 bg-primary-500/5'
                  : 'border-transparent text-gray-400 hover:text-gray-200 hover:bg-gray-800/50',
              ]"
            >
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M1.811 10.231c-.047 0-.058-.023-.035-.059l.246-.315c.023-.035.081-.058.128-.058h4.172c.046 0 .058.035.035.07l-.199.303c-.023.036-.082.07-.117.07zM.047 11.306c-.047 0-.059-.023-.035-.058l.245-.316c.023-.035.082-.058.129-.058h5.328c.047 0 .07.035.058.07l-.093.28c-.012.047-.058.07-.105.07zm2.828 1.075c-.047 0-.059-.035-.035-.07l.163-.292c.023-.035.07-.07.117-.07h2.337c.047 0 .07.035.07.082l-.023.28c0 .047-.047.082-.082.082zm12.129-2.36c-.736.187-1.239.327-1.963.514-.176.046-.187.058-.34-.117-.174-.199-.303-.327-.548-.444-.737-.362-1.45-.257-2.115.175-.795.514-1.204 1.274-1.192 2.22.011.935.654 1.706 1.577 1.835.795.105 1.46-.175 1.987-.77.105-.13.198-.27.315-.434H10.47c-.245 0-.304-.152-.222-.35.152-.362.432-.97.596-1.274a.315.315 0 01.292-.187h4.253c-.023.316-.023.631-.07.947a4.983 4.983 0 01-.958 2.29c-.841 1.11-1.94 1.8-3.33 1.986-1.145.152-2.209-.07-3.143-.77-.865-.655-1.356-1.52-1.484-2.595-.152-1.274.222-2.419.993-3.424.83-1.086 1.928-1.776 3.272-2.02 1.098-.2 2.15-.07 3.096.571.62.41 1.063.97 1.356 1.648.07.105.023.164-.117.2m3.868 6.461c-1.064-.024-2.034-.328-2.852-1.029a3.665 3.665 0 01-1.262-2.255c-.21-1.32.152-2.489.947-3.529.853-1.122 1.881-1.706 3.272-1.95 1.192-.21 2.314-.095 3.33.595.923.63 1.496 1.484 1.648 2.605.198 1.578-.257 2.863-1.344 3.962-.771.783-1.718 1.273-2.805 1.495-.315.06-.63.07-.934.106zm2.78-4.72c-.011-.153-.011-.27-.034-.387-.21-1.157-1.274-1.81-2.384-1.554-1.087.245-1.788.935-2.045 2.033-.21.912.234 1.835 1.075 2.21.643.28 1.285.244 1.905-.07.923-.48 1.425-1.228 1.484-2.233z"/>
              </svg>
              Go
            </button>
          </nav>
        </div>

        <!-- Content -->
        <div class="px-6 py-6">
          <!-- JavaScript SDK Content -->
          <div v-if="activeTab === 'js'" class="space-y-6">
            <!-- Step 1: Install -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">1</span>
                <h2 class="text-xl font-bold text-white font-display">Установка</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>npm</span>
                  <button @click="copyCode('npm install @pushx/js')" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>npm install @pushx/js</code></pre>
              </div>
            </section>

            <!-- Step 2: Client (Browser) -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">2</span>
                <h2 class="text-xl font-bold text-white font-display">Клиент (браузер)</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>JavaScript</span>
                  <button @click="copyCode(jsClientCode)" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>{{ jsClientCode }}</code></pre>
              </div>
            </section>

            <!-- Step 3: Server (Node.js) -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">3</span>
                <h2 class="text-xl font-bold text-white font-display">Сервер (Node.js)</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>Node.js</span>
                  <button @click="copyCode(jsServerCode)" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>{{ jsServerCode }}</code></pre>
              </div>
            </section>

            <!-- Full docs link -->
            <div class="bg-gray-800/50 rounded-xl p-5 border border-gray-700 flex items-center justify-between">
              <div>
                <h3 class="font-semibold text-white mb-1">Нужно больше?</h3>
                <p class="text-sm text-gray-400">Приватные каналы, presence, обработка ошибок, примеры для Vue/React</p>
              </div>
              <a
                href="https://pushx.pro/docs/sdks/js-sdk"
                target="_blank"
                class="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
              >
                Полная документация
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
              </a>
            </div>
          </div>

          <!-- PHP SDK Content -->
          <div v-if="activeTab === 'php'" class="space-y-6">
            <!-- Step 1: Install -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">1</span>
                <h2 class="text-xl font-bold text-white font-display">Установка</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>Composer</span>
                  <button @click="copyCode('composer require pushx/php-sdk')" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>composer require pushx/php-sdk</code></pre>
              </div>
              <p class="text-sm text-gray-400 mt-2">
                <a href="https://packagist.org/packages/pushx/php-sdk" target="_blank" class="text-primary-400 hover:underline inline-flex items-center gap-1">
                  📦 Packagist
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg>
                </a>
                · Требования: PHP >= 8.1
              </p>
            </section>

            <!-- Step 2: Initialize -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">2</span>
                <h2 class="text-xl font-bold text-white font-display">Инициализация</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>PHP</span>
                  <button @click="copyCode(phpInitCode)" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>{{ phpInitCode }}</code></pre>
              </div>
            </section>

            <!-- Step 3: Send message -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">3</span>
                <h2 class="text-xl font-bold text-white font-display">Отправка сообщения</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>PHP</span>
                  <button @click="copyCode(phpTriggerCode)" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>{{ phpTriggerCode }}</code></pre>
              </div>
            </section>

            <!-- Full docs link -->
            <div class="bg-gray-800/50 rounded-xl p-5 border border-gray-700 flex items-center justify-between">
              <div>
                <h3 class="font-semibold text-white mb-1">Нужно больше?</h3>
                <p class="text-sm text-gray-400">Авторизация каналов, presence, интеграция с Laravel</p>
              </div>
              <a
                href="https://pushx.pro/docs/sdks/php-sdk"
                target="_blank"
                class="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
              >
                Полная документация
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
              </a>
            </div>
          </div>

          <!-- Python SDK Content -->
          <div v-if="activeTab === 'python'" class="space-y-6">
            <!-- Step 1: Install -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">1</span>
                <h2 class="text-xl font-bold text-white font-display">Установка</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>pip</span>
                  <button @click="copyCode('pip install pushx')" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>pip install pushx</code></pre>
              </div>
              <p class="text-sm text-gray-400 mt-2">Требования: Python >= 3.8</p>
            </section>

            <!-- Step 2: Server -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">2</span>
                <h2 class="text-xl font-bold text-white font-display">Отправка с сервера</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>Python</span>
                  <button @click="copyCode(pythonServerCode)" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>{{ pythonServerCode }}</code></pre>
              </div>
            </section>

            <!-- Step 3: Async -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">3</span>
                <h2 class="text-xl font-bold text-white font-display">Асинхронный вариант</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>Python (asyncio)</span>
                  <button @click="copyCode(pythonAsyncCode)" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>{{ pythonAsyncCode }}</code></pre>
              </div>
            </section>

            <!-- Full docs link -->
            <div class="bg-gray-800/50 rounded-xl p-5 border border-gray-700 flex items-center justify-between">
              <div>
                <h3 class="font-semibold text-white mb-1">Нужно больше?</h3>
                <p class="text-sm text-gray-400">Django, FastAPI интеграции, async/await, вебхуки</p>
              </div>
              <a
                href="https://pushx.pro/docs/sdks/python"
                target="_blank"
                class="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
              >
                Полная документация
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
              </a>
            </div>
          </div>

          <!-- C# SDK Content -->
          <div v-if="activeTab === 'csharp'" class="space-y-6">
            <!-- Step 1: Install -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">1</span>
                <h2 class="text-xl font-bold text-white font-display">Установка</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>NuGet</span>
                  <button @click="copyCode('dotnet add package PushX')" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>dotnet add package PushX</code></pre>
              </div>
              <p class="text-sm text-gray-400 mt-2">Требования: .NET 6.0+</p>
            </section>

            <!-- Step 2: Server -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">2</span>
                <h2 class="text-xl font-bold text-white font-display">Отправка сообщений</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>C#</span>
                  <button @click="copyCode(csharpServerCode)" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>{{ csharpServerCode }}</code></pre>
              </div>
            </section>

            <!-- Step 3: ASP.NET -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">3</span>
                <h2 class="text-xl font-bold text-white font-display">Интеграция с ASP.NET</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>C# (ASP.NET Core)</span>
                  <button @click="copyCode(csharpAspNetCode)" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>{{ csharpAspNetCode }}</code></pre>
              </div>
            </section>

            <!-- Full docs link -->
            <div class="bg-gray-800/50 rounded-xl p-5 border border-gray-700 flex items-center justify-between">
              <div>
                <h3 class="font-semibold text-white mb-1">Нужно больше?</h3>
                <p class="text-sm text-gray-400">ASP.NET Core интеграция, DI, вебхуки</p>
              </div>
              <a
                href="https://pushx.pro/docs/sdks/csharp"
                target="_blank"
                class="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
              >
                Полная документация
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
              </a>
            </div>
          </div>

          <!-- Java SDK Content -->
          <div v-if="activeTab === 'java'" class="space-y-6">
            <!-- Step 1: Install -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">1</span>
                <h2 class="text-xl font-bold text-white font-display">Установка</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>Maven</span>
                  <button @click="copyCode(javaMavenCode)" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>{{ javaMavenCode }}</code></pre>
              </div>
              <p class="text-sm text-gray-400 mt-2">Требования: Java 11+</p>
            </section>

            <!-- Step 2: Server -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">2</span>
                <h2 class="text-xl font-bold text-white font-display">Отправка сообщений</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>Java</span>
                  <button @click="copyCode(javaServerCode)" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>{{ javaServerCode }}</code></pre>
              </div>
            </section>

            <!-- Step 3: Spring Boot -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">3</span>
                <h2 class="text-xl font-bold text-white font-display">Spring Boot интеграция</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>Java (Spring Boot)</span>
                  <button @click="copyCode(javaSpringCode)" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>{{ javaSpringCode }}</code></pre>
              </div>
            </section>

            <!-- Full docs link -->
            <div class="bg-gray-800/50 rounded-xl p-5 border border-gray-700 flex items-center justify-between">
              <div>
                <h3 class="font-semibold text-white mb-1">Нужно больше?</h3>
                <p class="text-sm text-gray-400">Spring Boot интеграция, Maven/Gradle, вебхуки</p>
              </div>
              <a
                href="https://pushx.pro/docs/sdks/java"
                target="_blank"
                class="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
              >
                Полная документация
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
              </a>
            </div>
          </div>

          <!-- Swift SDK Content -->
          <div v-if="activeTab === 'swift'" class="space-y-6">
            <!-- Step 1: Install -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">1</span>
                <h2 class="text-xl font-bold text-white font-display">Установка</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>Swift Package Manager</span>
                  <button @click="copyCode(swiftPackageCode)" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>{{ swiftPackageCode }}</code></pre>
              </div>
              <p class="text-sm text-gray-400 mt-2">Требования: iOS 13+ / macOS 10.15+</p>
            </section>

            <!-- Step 2: Client -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">2</span>
                <h2 class="text-xl font-bold text-white font-display">Подключение в iOS/macOS</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>Swift</span>
                  <button @click="copyCode(swiftClientCode)" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>{{ swiftClientCode }}</code></pre>
              </div>
            </section>

            <!-- Step 3: SwiftUI -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">3</span>
                <h2 class="text-xl font-bold text-white font-display">Пример со SwiftUI</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>SwiftUI</span>
                  <button @click="copyCode(swiftUICode)" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>{{ swiftUICode }}</code></pre>
              </div>
            </section>

            <!-- Full docs link -->
            <div class="bg-gray-800/50 rounded-xl p-5 border border-gray-700 flex items-center justify-between">
              <div>
                <h3 class="font-semibold text-white mb-1">Нужно больше?</h3>
                <p class="text-sm text-gray-400">SwiftUI, Vapor интеграция, WebSocket клиент</p>
              </div>
              <a
                href="https://pushx.pro/docs/sdks/swift"
                target="_blank"
                class="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
              >
                Полная документация
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
              </a>
            </div>
          </div>

          <!-- Go SDK Content -->
          <div v-if="activeTab === 'go'" class="space-y-6">
            <!-- Step 1: Install -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">1</span>
                <h2 class="text-xl font-bold text-white font-display">Установка</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>go get</span>
                  <button @click="copyCode('go get github.com/pushx/pushx-go')" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>go get github.com/pushx/pushx-go</code></pre>
              </div>
              <p class="text-sm text-gray-400 mt-2">Требования: Go 1.18+</p>
            </section>

            <!-- Step 2: Server -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">2</span>
                <h2 class="text-xl font-bold text-white font-display">Отправка сообщений</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>Go</span>
                  <button @click="copyCode(goServerCode)" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>{{ goServerCode }}</code></pre>
              </div>
            </section>

            <!-- Step 3: HTTP Handler -->
            <section>
              <div class="flex items-center gap-3 mb-4">
                <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-bold text-sm">3</span>
                <h2 class="text-xl font-bold text-white font-display">HTTP Handler пример</h2>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span>Go (net/http)</span>
                  <button @click="copyCode(goHttpCode)" class="copy-btn">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
                <pre class="code-block"><code>{{ goHttpCode }}</code></pre>
              </div>
            </section>

            <!-- Full docs link -->
            <div class="bg-gray-800/50 rounded-xl p-5 border border-gray-700 flex items-center justify-between">
              <div>
                <h3 class="font-semibold text-white mb-1">Нужно больше?</h3>
                <p class="text-sm text-gray-400">Gin/Echo интеграция, HTTP handlers, вебхуки</p>
              </div>
              <a
                href="https://pushx.pro/docs/sdks/go"
                target="_blank"
                class="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
              >
                Полная документация
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Channel Name Format -->
      <div v-if="selectedApp" class="card">
        <div class="px-6 py-4 border-b border-gray-800">
          <h2 class="text-lg font-semibold text-white font-display">Формат имени канала</h2>
        </div>
        <div class="px-6 py-5 space-y-5">
          <!-- Warning -->
          <div class="bg-primary-500/10 border border-primary-500/30 rounded-xl p-4">
            <div class="flex items-start gap-3">
              <svg class="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div>
                <p class="text-sm text-primary-300">
                  <strong>Автоматическая изоляция:</strong> Все каналы автоматически получают префикс с вашим App Key для изоляции между приложениями.
                </p>
              </div>
            </div>
          </div>

          <!-- Format explanation -->
          <div class="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
            <h4 class="font-medium text-white mb-3">Как это работает</h4>
            <p class="text-sm text-gray-400 mb-4">
              SDK автоматически добавляет ваш <code class="text-primary-400">App Key</code> к имени канала. Вам не нужно делать это вручную.
            </p>
            <div class="space-y-3">
              <div class="flex items-center gap-4 text-sm">
                <span class="text-gray-500 w-24">Вы пишете:</span>
                <code class="text-green-400 bg-green-500/10 px-3 py-1 rounded">my-channel</code>
              </div>
              <div class="flex items-center gap-4 text-sm">
                <span class="text-gray-500 w-24">SDK использует:</span>
                <code class="text-primary-400 bg-primary-500/10 px-3 py-1 rounded">{{ selectedApp?.key || 'app_key' }}:my-channel</code>
              </div>
            </div>
          </div>

          <!-- Why needed -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div class="bg-gray-800/30 p-4 rounded-lg border border-gray-700/50">
              <div class="flex items-center gap-2 mb-2">
                <svg class="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                </svg>
                <span class="text-sm font-medium text-white">Изоляция</span>
              </div>
              <p class="text-xs text-gray-400">Каналы разных приложений не пересекаются</p>
            </div>
            <div class="bg-gray-800/30 p-4 rounded-lg border border-gray-700/50">
              <div class="flex items-center gap-2 mb-2">
                <svg class="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                </svg>
                <span class="text-sm font-medium text-white">Безопасность</span>
              </div>
              <p class="text-xs text-gray-400">Невозможно подписаться на чужой канал</p>
            </div>
            <div class="bg-gray-800/30 p-4 rounded-lg border border-gray-700/50">
              <div class="flex items-center gap-2 mb-2">
                <svg class="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
                <span class="text-sm font-medium text-white">Простота</span>
              </div>
              <p class="text-xs text-gray-400">Не нужно думать о коллизиях имён</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Channel Types -->
      <div v-if="selectedApp" class="card">
        <div class="px-6 py-4 border-b border-gray-800">
          <h2 class="text-lg font-semibold text-white font-display">Типы каналов</h2>
        </div>
        <div class="px-6 py-5">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="bg-gray-800/50 p-5 rounded-xl border border-gray-700">
              <div class="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center mb-3">
                <svg class="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064"></path>
                </svg>
              </div>
              <h3 class="font-semibold text-white mb-2">Публичные</h3>
              <p class="text-sm text-gray-400 mb-3">Не требуют аутентификации. Доступны всем.</p>
              <code class="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded block mb-1">my-channel</code>
              <code class="text-xs text-gray-500 bg-gray-700/50 px-2 py-1 rounded block">→ {{ selectedApp?.key || 'app_key' }}:my-channel</code>
            </div>
            <div class="bg-gray-800/50 p-5 rounded-xl border border-gray-700">
              <div class="w-10 h-10 rounded-lg bg-accent-red/20 flex items-center justify-center mb-3">
                <svg class="w-5 h-5 text-accent-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                </svg>
              </div>
              <h3 class="font-semibold text-white mb-2">Приватные</h3>
              <p class="text-sm text-gray-400 mb-3">Требуют подпись с сервера.</p>
              <code class="text-xs text-accent-red bg-accent-red/10 px-2 py-1 rounded block mb-1">private-user-123</code>
              <code class="text-xs text-gray-500 bg-gray-700/50 px-2 py-1 rounded block">→ {{ selectedApp?.key || 'app_key' }}:private-user-123</code>
            </div>
            <div class="bg-gray-800/50 p-5 rounded-xl border border-gray-700">
              <div class="w-10 h-10 rounded-lg bg-accent-yellow/20 flex items-center justify-center mb-3">
                <svg class="w-5 h-5 text-accent-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
              </div>
              <h3 class="font-semibold text-white mb-2">Presence</h3>
              <p class="text-sm text-gray-400 mb-3">Отслеживание кто онлайн.</p>
              <code class="text-xs text-accent-yellow bg-accent-yellow/10 px-2 py-1 rounded block mb-1">presence-room-456</code>
              <code class="text-xs text-gray-500 bg-gray-700/50 px-2 py-1 rounded block">→ {{ selectedApp?.key || 'app_key' }}:presence-room-456</code>
            </div>
          </div>
        </div>
      </div>

      <!-- Help Links -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <a
          href="https://pushx.pro/docs/architecture"
          target="_blank"
          class="card px-5 py-4 hover:border-gray-600 transition-colors group"
        >
          <div class="flex items-center gap-4">
            <div class="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
              <svg class="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
              </svg>
            </div>
            <div>
              <h3 class="font-semibold text-white group-hover:text-primary-400 transition-colors">Архитектура</h3>
              <p class="text-sm text-gray-400">Как устроен pushx.pro</p>
            </div>
          </div>
        </a>
        <a
          href="https://pushx.pro/docs/api-security"
          target="_blank"
          class="card px-5 py-4 hover:border-gray-600 transition-colors group"
        >
          <div class="flex items-center gap-4">
            <div class="w-10 h-10 rounded-lg bg-accent-red/20 flex items-center justify-center">
              <svg class="w-5 h-5 text-accent-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
              </svg>
            </div>
            <div>
              <h3 class="font-semibold text-white group-hover:text-primary-400 transition-colors">Безопасность</h3>
              <p class="text-sm text-gray-400">HMAC подписи и защита</p>
            </div>
          </div>
        </a>
        <a
          href="https://pushx.pro/docs/how-it-works"
          target="_blank"
          class="card px-5 py-4 hover:border-gray-600 transition-colors group"
        >
          <div class="flex items-center gap-4">
            <div class="w-10 h-10 rounded-lg bg-accent-yellow/20 flex items-center justify-center">
              <svg class="w-5 h-5 text-accent-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
              </svg>
            </div>
            <div>
              <h3 class="font-semibold text-white group-hover:text-primary-400 transition-colors">Как это работает</h3>
              <p class="text-sm text-gray-400">Детальное описание</p>
            </div>
          </div>
        </a>
      </div>
    </div>

    <!-- Copy notification -->
    <Transition name="fade">
      <div
        v-if="showCopyNotification"
        class="fixed bottom-6 right-6 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-50"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        {{ copyNotificationText }}
      </div>
    </Transition>
  </div>
</template>

<script>
import { useApplicationsStore } from '../stores/applications';
import { copyText } from '../services/clipboard';

export default {
  name: 'SDKDocumentation',
  setup() {
    const applicationsStore = useApplicationsStore();
    return { applicationsStore };
  },
  data() {
    return {
      activeTab: 'js',
      selectedAppId: null,
      showSecret: false,
      // Секрет запрашивается с сервера только по кнопке «Показать»
      revealedSecret: null,
      secretLoading: false,
      showCopyNotification: false,
      copyNotificationText: '',
    };
  },
  computed: {
    applications() {
      return this.applicationsStore.applicationsList;
    },
    loading() {
      return this.applicationsStore.isLoading;
    },
    selectedApp() {
      if (!this.selectedAppId) return null;
      return this.applications.find(app => app.id === this.selectedAppId);
    },
    snippetSecret() {
      return this.showSecret && this.revealedSecret ? this.revealedSecret : 'your-app-secret';
    },
    jsClientCode() {
      const key = this.selectedApp?.key || 'your-app-key';
      return `import PushXClient from '@pushx/js';

const pushx = new PushXClient({
  appKey: '${key}',
  wsUrl: 'wss://ws.pushx.pro/app/${key}',
  authEndpoint: 'https://your-backend.com/pushx/auth',
  
  // JWT авторизация (опционально)
  getAuthHeaders: () => ({
    'Authorization': 'Bearer ' + localStorage.getItem('token')
  })
});

// Подключение (возвращает Promise)
await pushx.connect();

// Подписка на канал
const channel = pushx.subscribe('my-channel');

channel.on('message', (data) => {
  console.log('Получено:', data);
});`;
    },
    jsServerCode() {
      const key = this.selectedApp?.key || 'your-app-key';
      const secret = this.snippetSecret;
      return `import { PushXServer } from '@pushx/js';

const pushx = new PushXServer({
  appKey: '${key}',
  appSecret: '${secret}',
  apiUrl: 'https://api.pushx.pro'
});

// Отправка сообщения
await pushx.trigger('my-channel', 'message', {
  text: 'Hello World!'
});`;
    },
    phpInitCode() {
      const key = this.selectedApp?.key || 'your-app-key';
      const secret = this.snippetSecret;
      return `<?php
use PushX\\PushXClient;

$pushx = new PushXClient(
    '${key}',
    '${secret}',
    'https://api.pushx.pro'
);`;
    },
    phpTriggerCode() {
      return `// Отправка события в канал
$result = $pushx->trigger(
    'my-channel',
    'message',
    ['text' => 'Hello World!']
);`;
    },
    // Python
    pythonServerCode() {
      const key = this.selectedApp?.key || 'your-app-key';
      const secret = this.snippetSecret;
      return `import requests
import hmac
import hashlib
import json
import time

APP_KEY = "${key}"
APP_SECRET = "${secret}"
API_URL = "https://api.pushx.pro"

def trigger(channel: str, event: str, data: dict):
    body = json.dumps({
        "channel": channel,
        "event": event,
        "data": data
    })
    
    timestamp = str(int(time.time()))
    signature = hmac.new(
        APP_SECRET.encode(),
        f"{body}{timestamp}".encode(),
        hashlib.sha256
    ).hexdigest()
    
    response = requests.post(
        f"{API_URL}/apps/{APP_KEY}/events",
        headers={
            "Content-Type": "application/json",
            "X-PushX-Signature": signature,
            "X-PushX-Timestamp": timestamp
        },
        data=body
    )
    return response.json()

# Отправка сообщения
result = trigger("my-channel", "message", {"text": "Hello World!"})`;
    },
    pythonAsyncCode() {
      const key = this.selectedApp?.key || 'your-app-key';
      const secret = this.snippetSecret;
      return `import aiohttp
import asyncio

async def trigger_async(channel: str, event: str, data: dict):
    async with aiohttp.ClientSession() as session:
        body = json.dumps({
            "channel": channel,
            "event": event, 
            "data": data
        })
        
        timestamp = str(int(time.time()))
        signature = hmac.new(
            "${secret}".encode(),
            f"{body}{timestamp}".encode(),
            hashlib.sha256
        ).hexdigest()
        
        async with session.post(
            f"https://api.pushx.pro/apps/${key}/events",
            headers={
                "Content-Type": "application/json",
                "X-PushX-Signature": signature,
                "X-PushX-Timestamp": timestamp
            },
            data=body
        ) as resp:
            return await resp.json()

# Использование
asyncio.run(trigger_async("my-channel", "message", {"text": "Hello!"}))`;
    },
    // C#
    csharpServerCode() {
      const key = this.selectedApp?.key || 'your-app-key';
      const secret = this.snippetSecret;
      return `using System.Security.Cryptography;
using System.Text;
using System.Text.Json;

public class PushXClient
{
    private readonly string _appKey = "${key}";
    private readonly string _appSecret = "${secret}";
    private readonly HttpClient _httpClient = new();

    public async Task TriggerAsync(string channel, string eventName, object data)
    {
        var body = JsonSerializer.Serialize(new {
            channel,
            @event = eventName,
            data
        });

        var timestamp = DateTimeOffset.UtcNow.ToUnixTimeSeconds().ToString();
        var signature = ComputeSignature(body, timestamp);

        var request = new HttpRequestMessage(HttpMethod.Post, 
            $"https://api.pushx.pro/apps/{_appKey}/events");
        request.Headers.Add("X-PushX-Signature", signature);
        request.Headers.Add("X-PushX-Timestamp", timestamp);
        request.Content = new StringContent(body, Encoding.UTF8, "application/json");

        await _httpClient.SendAsync(request);
    }

    private string ComputeSignature(string body, string timestamp)
    {
        using var hmac = new HMACSHA256(Encoding.UTF8.GetBytes(_appSecret));
        var hash = hmac.ComputeHash(Encoding.UTF8.GetBytes(body + timestamp));
        return Convert.ToHexString(hash).ToLower();
    }
}

// Использование
var client = new PushXClient();
await client.TriggerAsync("my-channel", "message", new { text = "Hello World!" });`;
    },
    csharpAspNetCode() {
      return `// Program.cs
builder.Services.AddSingleton<PushXClient>();

// Controller
[ApiController]
[Route("api/[controller]")]
public class NotificationsController : ControllerBase
{
    private readonly PushXClient _pushx;

    public NotificationsController(PushXClient pushx)
    {
        _pushx = pushx;
    }

    [HttpPost("send")]
    public async Task<IActionResult> SendNotification([FromBody] NotificationDto dto)
    {
        await _pushx.TriggerAsync(
            $"user-{dto.UserId}",
            "notification",
            new { message = dto.Message, type = dto.Type }
        );
        
        return Ok(new { success = true });
    }
}`;
    },
    // Java
    javaMavenCode() {
      return `<dependency>
    <groupId>pro.pushx</groupId>
    <artifactId>pushx-java</artifactId>
    <version>1.0.0</version>
</dependency>`;
    },
    javaServerCode() {
      const key = this.selectedApp?.key || 'your-app-key';
      const secret = this.snippetSecret;
      return `import javax.crypto.Mac;
import javax.crypto.spec.SecretKeySpec;
import java.net.http.*;
import java.net.URI;

public class PushXClient {
    private static final String APP_KEY = "${key}";
    private static final String APP_SECRET = "${secret}";
    private static final String API_URL = "https://api.pushx.pro";

    public void trigger(String channel, String event, Map<String, Object> data) 
            throws Exception {
        ObjectMapper mapper = new ObjectMapper();
        String body = mapper.writeValueAsString(Map.of(
            "channel", channel,
            "event", event,
            "data", data
        ));

        String timestamp = String.valueOf(System.currentTimeMillis() / 1000);
        String signature = computeSignature(body + timestamp);

        HttpClient client = HttpClient.newHttpClient();
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(API_URL + "/apps/" + APP_KEY + "/events"))
            .header("Content-Type", "application/json")
            .header("X-PushX-Signature", signature)
            .header("X-PushX-Timestamp", timestamp)
            .POST(HttpRequest.BodyPublishers.ofString(body))
            .build();

        client.send(request, HttpResponse.BodyHandlers.ofString());
    }

    private String computeSignature(String data) throws Exception {
        Mac mac = Mac.getInstance("HmacSHA256");
        mac.init(new SecretKeySpec(APP_SECRET.getBytes(), "HmacSHA256"));
        byte[] hash = mac.doFinal(data.getBytes());
        return bytesToHex(hash);
    }
}

// Использование
var client = new PushXClient();
client.trigger("my-channel", "message", Map.of("text", "Hello World!"));`;
    },
    javaSpringCode() {
      return `@Service
public class NotificationService {
    
    private final PushXClient pushxClient;

    public NotificationService(PushXClient pushxClient) {
        this.pushxClient = pushxClient;
    }

    public void sendToUser(Long userId, String message) {
        pushxClient.trigger(
            "user-" + userId,
            "notification",
            Map.of(
                "message", message,
                "timestamp", Instant.now().toString()
            )
        );
    }
}

@RestController
@RequestMapping("/api/notifications")
public class NotificationController {
    
    @Autowired
    private NotificationService notificationService;

    @PostMapping("/send")
    public ResponseEntity<?> send(@RequestBody NotificationRequest request) {
        notificationService.sendToUser(request.getUserId(), request.getMessage());
        return ResponseEntity.ok(Map.of("success", true));
    }
}`;
    },
    // Swift
    swiftPackageCode() {
      return `// Package.swift
dependencies: [
    .package(url: "https://github.com/pushx/pushx-swift.git", from: "1.0.0")
]

// Или добавьте через Xcode:
// File → Add Packages → https://github.com/pushx/pushx-swift.git`;
    },
    swiftClientCode() {
      const key = this.selectedApp?.key || 'your-app-key';
      return `import Foundation
import Combine

class PushXClient: ObservableObject {
    private var webSocket: URLSessionWebSocketTask?
    private var cancellables = Set<AnyCancellable>()
    
    @Published var isConnected = false
    @Published var lastMessage: [String: Any]?
    
    let appKey = "${key}"
    
    func connect() {
        let url = URL(string: "wss://ws.pushx.pro/app/\\(appKey)")!
        webSocket = URLSession.shared.webSocketTask(with: url)
        webSocket?.resume()
        isConnected = true
        receiveMessage()
    }
    
    func subscribe(channel: String) {
        let message = [
            "event": "pushx:subscribe",
            "data": ["channel": channel]
        ] as [String: Any]
        
        if let data = try? JSONSerialization.data(withJSONObject: message),
           let string = String(data: data, encoding: .utf8) {
            webSocket?.send(.string(string)) { _ in }
        }
    }
    
    private func receiveMessage() {
        webSocket?.receive { [weak self] result in
            switch result {
            case .success(let message):
                if case .string(let text) = message,
                   let data = text.data(using: .utf8),
                   let json = try? JSONSerialization.jsonObject(with: data) as? [String: Any] {
                    DispatchQueue.main.async {
                        self?.lastMessage = json
                    }
                }
                self?.receiveMessage()
            case .failure:
                self?.isConnected = false
            }
        }
    }
    
    func disconnect() {
        webSocket?.cancel(with: .goingAway, reason: nil)
        isConnected = false
    }
}`;
    },
    swiftUICode() {
      return `import SwiftUI

struct ContentView: View {
    @StateObject private var pushx = PushXClient()
    @State private var messages: [String] = []
    
    var body: some View {
        VStack(spacing: 20) {
            // Статус подключения
            HStack {
                Circle()
                    .fill(pushx.isConnected ? Color.green : Color.red)
                    .frame(width: 10, height: 10)
                Text(pushx.isConnected ? "Подключено" : "Отключено")
            }
            
            // Список сообщений
            List(messages, id: \\.self) { message in
                Text(message)
            }
            
            // Кнопки управления
            HStack {
                Button("Подключиться") {
                    pushx.connect()
                    pushx.subscribe(channel: "notifications")
                }
                .disabled(pushx.isConnected)
                
                Button("Отключиться") {
                    pushx.disconnect()
                }
                .disabled(!pushx.isConnected)
            }
        }
        .padding()
        .onChange(of: pushx.lastMessage) { _, newValue in
            if let data = newValue?["data"] as? [String: Any],
               let text = data["message"] as? String {
                messages.append(text)
            }
        }
    }
}`;
    },
    // Go
    goServerCode() {
      const key = this.selectedApp?.key || 'your-app-key';
      const secret = this.snippetSecret;
      return `package main

import (
    "bytes"
    "crypto/hmac"
    "crypto/sha256"
    "encoding/hex"
    "encoding/json"
    "fmt"
    "net/http"
    "strconv"
    "time"
)

const (
    appKey    = "${key}"
    appSecret = "${secret}"
    apiURL    = "https://api.pushx.pro"
)

type PushXClient struct {
    httpClient *http.Client
}

func NewPushXClient() *PushXClient {
    return &PushXClient{
        httpClient: &http.Client{Timeout: 10 * time.Second},
    }
}

func (c *PushXClient) Trigger(channel, event string, data interface{}) error {
    payload := map[string]interface{}{
        "channel": channel,
        "event":   event,
        "data":    data,
    }
    
    body, _ := json.Marshal(payload)
    timestamp := strconv.FormatInt(time.Now().Unix(), 10)
    signature := c.computeSignature(string(body), timestamp)

    req, _ := http.NewRequest("POST", 
        fmt.Sprintf("%s/apps/%s/events", apiURL, appKey),
        bytes.NewBuffer(body))
    
    req.Header.Set("Content-Type", "application/json")
    req.Header.Set("X-PushX-Signature", signature)
    req.Header.Set("X-PushX-Timestamp", timestamp)

    _, err := c.httpClient.Do(req)
    return err
}

func (c *PushXClient) computeSignature(body, timestamp string) string {
    h := hmac.New(sha256.New, []byte(appSecret))
    h.Write([]byte(body + timestamp))
    return hex.EncodeToString(h.Sum(nil))
}

func main() {
    client := NewPushXClient()
    client.Trigger("my-channel", "message", map[string]string{
        "text": "Hello World!",
    })
}`;
    },
    goHttpCode() {
      return `package main

import (
    "encoding/json"
    "net/http"
)

var pushx = NewPushXClient()

func notificationHandler(w http.ResponseWriter, r *http.Request) {
    var req struct {
        UserID  int64  \`json:"user_id"\`
        Message string \`json:"message"\`
    }
    
    if err := json.NewDecoder(r.Body).Decode(&req); err != nil {
        http.Error(w, err.Error(), http.StatusBadRequest)
        return
    }
    
    // Отправляем уведомление пользователю
    err := pushx.Trigger(
        fmt.Sprintf("user-%d", req.UserID),
        "notification",
        map[string]interface{}{
            "message": req.Message,
            "time":    time.Now().Format(time.RFC3339),
        },
    )
    
    if err != nil {
        http.Error(w, err.Error(), http.StatusInternalServerError)
        return
    }
    
    json.NewEncoder(w).Encode(map[string]bool{"success": true})
}

func main() {
    http.HandleFunc("/api/notify", notificationHandler)
    http.ListenAndServe(":8080", nil)
}`;
    },
  },
  watch: {
    // При смене приложения прячем секрет предыдущего
    selectedAppId() {
      this.showSecret = false;
      this.revealedSecret = null;
    },
    applications: {
      immediate: true,
      handler(apps) {
        if (apps.length > 0 && !this.selectedAppId) {
          this.selectedAppId = apps[0].id;
        }
      },
    },
  },
  mounted() {
    this.applicationsStore.fetchApplications();
  },
  methods: {
    async toggleSecret() {
      if (this.showSecret) {
        this.showSecret = false;
        return;
      }

      try {
        this.secretLoading = true;
        this.revealedSecret = await this.applicationsStore.fetchSecret(this.selectedAppId);
        this.showSecret = true;
      } catch (err) {
        console.error('Failed to fetch secret:', err);
      } finally {
        this.secretLoading = false;
      }
    },
    async copySecret() {
      // Промис передаём сразу, без await: иначе Safari потеряет жест пользователя
      const secret = this.applicationsStore.fetchSecret(this.selectedAppId);
      await this.copyToClipboard(secret, 'App Secret');
    },
    async copyToClipboard(text, label) {
      try {
        await copyText(text);
        this.showNotification(`${label} скопирован`);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    },
    async copyCode(code) {
      try {
        await copyText(code);
        this.showNotification('Код скопирован');
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    },
    showNotification(text) {
      this.copyNotificationText = text;
      this.showCopyNotification = true;
      setTimeout(() => {
        this.showCopyNotification = false;
      }, 2000);
    },
  },
};
</script>

<style scoped>
.code-block-wrapper {
  @apply rounded-xl overflow-hidden border border-gray-700;
}

.code-block-header {
  @apply bg-gray-800 px-4 py-2 flex items-center justify-between text-sm text-gray-400 border-b border-gray-700;
}

.code-block {
  @apply bg-gray-900 p-4 overflow-x-auto m-0;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 13px;
  line-height: 1.6;
}

.code-block code {
  @apply text-gray-100;
  font-family: inherit;
}

.copy-btn {
  @apply text-gray-400 hover:text-primary-400 transition-colors p-1 rounded hover:bg-gray-700;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
